import { CancelledError, queryOptions, useQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { privateApi } from "../libs/axios";
import { STALE_TIME } from "../libs/react-query";
import { parseWith } from "../parse";
import { isApiError } from "../errors";
import { authKeys } from "./keys";
import { clearSessionHint, setSessionHint } from "./session-hint";
import { ProfileSchema, type Profile } from "./profile";

/**
 * GET /auth/me/ — the ambient identity bootstrap. Runs on `privateApi`, so on a fresh page
 * load (access token gone, refresh cookie present) the 401→refresh→retry interceptor
 * transparently mints a new token and this resolves with the profile; with no valid cookie it
 * rejects with an ApiError.
 *
 * `optionalAuth` is what makes it safe to ask this question from a PUBLIC page. This endpoint
 * failing means "nobody is signed in" — which is a legitimate answer here, not a lost session — so
 * the interceptor must not turn it into an app-wide redirect to login. Refresh is still attempted;
 * only the failure path stays local. Every caller (`useMe`, `ensureProfile`, `tryGetProfile`)
 * already treats rejection as "anonymous", so no caller needs to change.
 */
export async function getMe(): Promise<Profile> {
  // `<unknown>` is load-bearing: axios defaults its response generic to `any`, so an unannotated
  // `r.data` is `any` — and `no-explicit-any` cannot see an INFERRED any. That would let a
  // service skip `parseWith` and return `any` wearing the domain type's name, with green lint
  // and green tsc. Typing the call `unknown` makes the parse compiler-mandatory.
  const dto = await privateApi
    .get<unknown>("/auth/me/", { optionalAuth: true })
    .then((r) => r.data);
  return parseWith(ProfileSchema, dto, "GET /auth/me/");
}

/**
 * Shared query definition for the caller's profile. Used by the `useMe` hook AND by route
 * guards (via `ensureQueryData` in `beforeLoad`), so both read one cache entry. ownProfile
 * tier: bundle/verification changes must surface fast (near-zero staleTime). `retry: false` —
 * a 401 here means "not signed in", not a flaky call, so don't hammer /me.
 *
 * NO `enabled` — the documented carve-out to data-layer.md #4. This IS the query that
 * establishes whether anyone is signed in, so gating it on auth state would be circular.
 * Every other query in the app states its precondition.
 */
export const meQueryOptions = queryOptions({
  queryKey: authKeys.me(),
  queryFn: getMe,
  staleTime: STALE_TIME.ownProfile,
  retry: false,
});

/** Subscribe to the current profile inside React. */
export function useMe() {
  return useQuery(meQueryOptions);
}

/**
 * Ensure the profile is loaded (route guards). Returns cached data if present, otherwise
 * fetches. Propagates the ApiError when the caller is unauthenticated.
 */
export function ensureProfile(queryClient: QueryClient): Promise<Profile> {
  return queryClient.ensureQueryData(meQueryOptions);
}

/**
 * Guard-friendly variant: resolve the profile, or `null` when the caller is unauthenticated
 * (the interceptor already tried and failed to refresh). Non-auth errors (e.g. a contract
 * violation) propagate so they surface loudly instead of masquerading as "logged out".
 */
export async function tryGetProfile(
  queryClient: QueryClient
): Promise<Profile | null> {
  try {
    return await ensureProfile(queryClient);
  } catch (error) {
    if (isApiError(error)) return null;
    // The probe can be CANCELLED rather than rejected, and that is not an edge case — it is the
    // normal path for a visitor with no session. `/me` 401s, the interceptor's refresh also
    // 401s, so it calls `signalLogout()`, whose handler runs `clearProfile()` →
    // `removeQueries()` — which CANCELS the very request this line is awaiting. React Query
    // then rejects with `CancelledError` instead of the ApiError, and before this branch existed
    // that escaped straight out of the router's `beforeLoad` and tripped the error boundary:
    // an anonymous visitor hitting "/" saw a crash instead of the login screen.
    //
    // A cancelled probe cannot tell us who the caller is, which for a guard means exactly what
    // a 401 means — nobody is signed in.
    if (error instanceof CancelledError) return null;
    throw error;
  }
}

/** Seed the profile cache from a login response — skips an immediate /me round-trip. */
export function seedProfile(queryClient: QueryClient, profile: Profile): void {
  queryClient.setQueryData(authKeys.me(), profile);
  // A session now exists in this browser. Recorded HERE rather than in the login mutation so it
  // cannot be forgotten by a future caller that seeds a profile some other way.
  setSessionHint();
}

/** Invalidate the profile after an auth-affecting change (bundles apply on the next request). */
export function invalidateProfile(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: authKeys.me() });
}

/** Drop the cached profile (logout / session loss). */
export function clearProfile(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: authKeys.me() });
  // Paired with `seedProfile`. This is also what makes the hint self-correcting: every path that
  // discovers the session is gone — logout, a refresh that failed — comes through here.
  clearSessionHint();
}
